/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import isNil from 'lodash/isNil';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../components/common/map/GoogleMapSingleton';
import FlightLegServiceAlerts, { ServiceAlert } from '../../shared/entities/flightLegServiceAlerts';
import { Action as GlobalAction } from '../actions';
import { Action, ActionCreators } from './actions';
import { getServiceAlertMapType, getServiceAlertNetworkType } from '../../utils/BeamUtils';

export interface ServiceAlertContextType {
  // Used in Full Tail View (/servicealertsallusers endpoint) and Performance Outlook (/servicealerts endpoint)
  readonly flightLegServiceAlerts?: { [flightLegId: number]: FlightLegServiceAlerts };
  readonly isRequestingFlightLegServiceAlerts: boolean;
  readonly requestingFlightLegServiceAlertsError?: string;
}

interface ServiceAlertMapContext {
  readonly isServiceAlertDialogOpen: boolean;
}

export interface State extends ServiceAlertContextType {
  readonly uiState: { [mapId: string]: ServiceAlertMapContext };
}

const defaultServiceAlertsMapContext: ServiceAlertMapContext = {
  isServiceAlertDialogOpen: false,
};

export const defaultServiceAlertsContextState: ServiceAlertContextType = {
  flightLegServiceAlerts: undefined,
  isRequestingFlightLegServiceAlerts: false,
  requestingFlightLegServiceAlertsError: undefined,
};

export const initialFlightLegServiceAlertsState: State = {
  ...defaultServiceAlertsContextState,
  uiState: {
    [GOOGLE_MAP_INSTANCE_KEY]: defaultServiceAlertsMapContext,
  },
};

// Reducer
export default function reducer(
  state: State = initialFlightLegServiceAlertsState,
  action: Action | GlobalAction,
): State {
  switch (action.type) {
    case ActionCreators.RequestServiceAlerts.type:
    case ActionCreators.RequestFlightLegServiceAlerts.type: {
      return {
        ...state,
        isRequestingFlightLegServiceAlerts: true,
        requestingFlightLegServiceAlertsError: undefined,
      };
    }
    case ActionCreators.ReceivedServiceAlertsSuccess.type:
    case ActionCreators.ReceivedFlightLegServiceAlertsSuccess.type: {
      let flightLegServiceAlerts: FlightLegServiceAlerts = { ...action.payload.response.response };
      flightLegServiceAlerts = {
        ...flightLegServiceAlerts,
        // TODO: Temporarily add network & mapTypes for Phase 1 contourType Refactoring. For Phase 2, add origin/destination network and mapType to the API response and remove the enrichment.
        originBeamNetwork: flightLegServiceAlerts.originBeamType ? getServiceAlertNetworkType(flightLegServiceAlerts.originBeamType) : undefined,
        originBeamMapType: flightLegServiceAlerts.originBeamType ? getServiceAlertMapType(flightLegServiceAlerts.originBeamType): undefined,
        destinationBeamNetwork: flightLegServiceAlerts.destinationBeamType ? getServiceAlertNetworkType(flightLegServiceAlerts.destinationBeamType) : undefined,
        destinationBeamMapType: flightLegServiceAlerts.destinationBeamType ? getServiceAlertMapType(flightLegServiceAlerts.destinationBeamType): undefined
      }
      if (!isNil(flightLegServiceAlerts.serviceAlerts)) {
        flightLegServiceAlerts.serviceAlerts = flightLegServiceAlerts.serviceAlerts.map(
          (flightLegServiceAlert: ServiceAlert, index: number) => {
            return {
              ...flightLegServiceAlert,
              // TODO: Temporarily add network & mapTypes for Phase 1 contourType Refactoring. For Phase 2, add login/logout network and mapType to the API response and remove the enrichment.
              loginBeamNetwork: flightLegServiceAlert.loginBeamType ? getServiceAlertNetworkType(flightLegServiceAlert.loginBeamType) : undefined,
              loginBeamMapType: flightLegServiceAlert.loginBeamType ? getServiceAlertMapType(flightLegServiceAlert.loginBeamType) : undefined,
              logoutBeamNetwork: flightLegServiceAlert.logoutBeamType ? getServiceAlertNetworkType(flightLegServiceAlert.logoutBeamType) : undefined,
              logoutBeamMapType: flightLegServiceAlert.logoutBeamType ? getServiceAlertMapType(flightLegServiceAlert.logoutBeamType) : undefined,
              id: index,
            };
          },
        );
      }
      return {
        ...state,
        flightLegServiceAlerts: {
          ...state.flightLegServiceAlerts,
          [flightLegServiceAlerts.id]: {
            ...flightLegServiceAlerts,
            serviceAlerts: flightLegServiceAlerts.serviceAlerts.map(
              (flightLegServiceAlert: ServiceAlert, index: number) => {
                return {
                  ...flightLegServiceAlert,
                  id: index,
                };
              },
            ),
          },
        },
        requestingFlightLegServiceAlertsError: undefined,
        isRequestingFlightLegServiceAlerts: false,
      };
    }

    case ActionCreators.ReceivedServiceAlertsError.type:
    case ActionCreators.ReceivedFlightLegServiceAlertsError.type:
      return {
        ...state,
        requestingFlightLegServiceAlertsError: action.payload.error,
        isRequestingFlightLegServiceAlerts: false,
      };

    case ActionCreators.ClearReceivedServiceAlertsError.type:
    case ActionCreators.ClearReceivedFlightLegServiceAlertsError.type: {
      return {
        ...state,
        requestingFlightLegServiceAlertsError: undefined,
      };
    }

    case ActionCreators.ClearServiceAlerts.type: {
      return {
        ...state,
        flightLegServiceAlerts: undefined,
      };
    }

    case ActionCreators.SetIsServiceAlertDialogOpen.type: {
      return {
        ...state,
        uiState: {
          ...state.uiState,
          [action.payload.mapId]: {
            ...state.uiState[action.payload.mapId],
            isServiceAlertDialogOpen: true,
          },
        },
      };
    }

    case ActionCreators.UnsetIsServiceAlertDialogOpen.type: {
      return {
        ...state,
        uiState: {
          ...state.uiState,
          [action.payload.mapId]: {
            ...state.uiState[action.payload.mapId],
            isServiceAlertDialogOpen: false,
          },
        },
      };
    }

    default:
      return state;
  }
}